import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiRequest } from '../api/client.js';
import type { PlatformName } from '../api/types.js';
import { Sparkline } from './dashboard/Sparkline.js';
import './dashboard.css';
import type { ConnectionEntry } from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { EmptyState } from '../components/EmptyState.js';
import { GettingStarted } from '../components/GettingStarted.js';
import { PlatformIcon } from '../components/PlatformIcon.js';
import { StatusPill } from '../components/StatusPill.js';
import { HelpHint } from '../components/Tooltip.js';

/**
 * Dashboard (ARCHITECTURE.md §64, §65).
 *
 * Answers the question an operator actually opens this page with: is my chat
 * flowing right now, and if not, which platform is the problem.
 *
 * Every figure comes from the server. There are still no invented metrics — no
 * "viewers", no "messages today" — because Connaro does not measure those and
 * CLAUDE_MASTER_PROMPT.md rules out numbers that look real and are not.
 *
 * This page previously told every operator that "platform integrations are not
 * yet available". That was true when it was written and false for months
 * afterwards, which is the failure mode of hand-written status text: it does
 * not know when it has stopped being true. What replaces it is derived from
 * the connections API, so it cannot drift.
 */

type ConnectionRow = ConnectionEntry;

function summarise(connections: ConnectionRow[]): {
  connected: ConnectionRow[];
  attention: ConnectionRow[];
  connectable: ConnectionRow[];
} {
  const connected = connections.filter((entry) => entry.connection !== null);

  return {
    connected,
    /*
     * Anything an operator must act on themselves. A reconnecting adapter is
     * NOT here: it recovers without help, and listing it would train people to
     * ignore this section.
     */
    attention: connected.filter(
      (entry) =>
        entry.connection?.status === 'REAUTH_REQUIRED' || entry.connection?.status === 'ERROR',
    ),
    connectable: connections.filter((entry) => entry.connectable && entry.connection === null),
  };
}

type Overview = {
  days: number;
  messages: {
    today: number;
    yesterday: number;
    week: number;
    daily: { day: string; messages: number }[];
    byPlatform: { platform: PlatformName; messages: number }[];
  };
  connected: number;
  platformCount: number;
  recent: {
    platform: PlatformName;
    eventType: string;
    displayName: string | null;
    displayAmount: string | null;
    occurredAt: string;
  }[];
  topChatters: { displayName: string; messages: number }[];
};

const EVENT_VERBS: Record<string, string> = {
  follow: 'followed',
  subscription: 'subscribed',
  membership: 'became a member',
  gift: 'gifted a subscription',
  donation: 'donated',
  raid: 'raided',
};

/**
 * How long ago, in the coarsest unit that is still true.
 *
 * "2 hours ago" rather than "2 hours 14 minutes": nobody reading a dashboard
 * needs the minutes, and the precision would imply the figure updates more
 * often than it does.
 */
function ago(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m ago`;
  if (seconds < 86_400) return `${String(Math.floor(seconds / 3600))}h ago`;
  return `${String(Math.floor(seconds / 86_400))}d ago`;
}

/**
 * One figure, sized so it reads as the point of the card.
 *
 * §11 of the redesign brief: the common SaaS failure is everything at 14px
 * medium grey. A number an operator came to see should not be the same weight
 * as the word describing it.
 *
 * `compare` renders a delta only when there is something to compare against.
 * A "0%" beside every metric on a quiet day is noise dressed as insight.
 */
function Metric({
  label,
  value,
  compare,
  of,
}: {
  label: string;
  value: number;
  compare?: number | undefined;
  of?: number | undefined;
}): React.JSX.Element {
  const delta = compare === undefined || compare === 0 ? null : Math.round(((value - compare) / compare) * 100);

  return (
    <div className="metric">
      <span className="metric__label">{label}</span>
      <span className="metric__value">
        {value.toLocaleString()}
        {of !== undefined && <span className="metric__of"> / {of.toLocaleString()}</span>}
      </span>
      {delta !== null && (
        <span className={`metric__delta metric__delta--${delta >= 0 ? 'up' : 'down'}`}>
          {delta >= 0 ? '+' : ''}
          {delta}% vs yesterday
        </span>
      )}
    </div>
  );
}

export function DashboardPage(): React.JSX.Element {
  const { me, activeWorkspace } = useSession();
  const [connections, setConnections] = useState<ConnectionRow[] | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);

  useEffect(() => {
    let cancelled = false;

    /*
     * Best effort, and deliberately not blocking the page.
     *
     * The activity figures are useful context; the platform list is the reason
     * an operator opened this page before a broadcast. A slow aggregate must
     * not hold that back, and a failed one must not blank it.
     */
    void apiRequest<Overview>('/dashboard/overview')
      .then(setOverview)
      .catch(() => {
        setOverview(null);
      });

    void apiRequest<{ connections: ConnectionEntry[] }>('/connections')
      .then((data) => {
        if (!cancelled) setConnections(data.connections);
      })
      .catch(() => {
        // A dashboard that cannot read connections still shows the workspace
        // facts it already has; an error banner here would be noise.
        if (!cancelled) setConnections([]);
      });

    return () => {
      cancelled = true;
    };
  }, [activeWorkspace?.id]);

  if (me === null) return <></>;

  if (activeWorkspace === null) {
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">Dashboard</h1>
          <p className="page__subtitle">Select a workspace to get started.</p>
        </header>
        <section className="panel">
          <div className="panel__body">
            <EmptyState title="No workspace selected">
              Choose a workspace from the switcher above to start working in it.
            </EmptyState>
          </div>
        </section>
      </>
    );
  }

  const summary = connections === null ? null : summarise(connections);

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Dashboard</h1>
        <p className="page__subtitle">Operating {activeWorkspace.name}.</p>
      </header>

      {/*
       * Attention first, and only when there is something to say. A permanent
       * "all good" banner is scrolled past within a week; an alert that appears
       * only when it matters keeps its meaning.
       */}
      {summary !== null && summary.attention.length > 0 ? (
        <div className="alert alert--warning" role="status">
          <strong>
            {summary.attention.length === 1
              ? '1 connection needs attention'
              : `${String(summary.attention.length)} connections need attention`}
            :
          </strong>{' '}
          {summary.attention.map((entry) => entry.displayName).join(', ')}.{' '}
          <Link to="/connections">Review connections</Link>
        </div>
      ) : null}

      {overview !== null && overview.messages.week > 0 && (
        <section className="panel" aria-labelledby="activity-heading">
          <div className="panel__header">
            <h2 className="panel__title" id="activity-heading">
              Activity
            </h2>
            <HelpHint
              label="what these counts cover"
              tip="Messages Connaro received and retained across every connected platform. Connaro holds no viewer or follower counts, so it does not report them."
            />
          </div>

          <div className="panel__body dash__activity">
            <div className="dash__metrics">
              <Metric label="Messages today" value={overview.messages.today} compare={overview.messages.yesterday} />
              <Metric label={`Last ${String(overview.days)} days`} value={overview.messages.week} />
              <Metric label="Platforms live" value={overview.connected} of={overview.platformCount} />
            </div>

            <Sparkline points={overview.messages.daily} />

            {overview.messages.byPlatform.length > 0 && (
              <ul className="dash__breakdown">
                {overview.messages.byPlatform.map((entry) => (
                  <li key={entry.platform}>
                    <PlatformIcon
                      platform={entry.platform}
                      displayName={entry.platform}
                      size={16}
                      label={entry.platform}
                    />
                    <span className="dash__breakdown-count">{entry.messages.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {overview !== null && (overview.recent.length > 0 || overview.topChatters.length > 0) && (
        <div className="dash__columns">
          {overview.recent.length > 0 && (
            <section className="panel" aria-labelledby="recent-heading">
              <div className="panel__header">
                <h2 className="panel__title" id="recent-heading">
                  Recent events
                </h2>
              </div>
              <ul className="panel__body dash__events">
                {overview.recent.map((event, index) => (
                  <li key={`${event.occurredAt}-${String(index)}`} className="dash__event">
                    <PlatformIcon
                      platform={event.platform}
                      displayName={event.platform}
                      size={16}
                      label={event.platform}
                    />
                    <span className="dash__event-text">
                      <strong>{event.displayName ?? 'Someone'}</strong>{' '}
                      {EVENT_VERBS[event.eventType] ?? event.eventType}
                      {event.displayAmount !== null && <> {event.displayAmount}</>}
                    </span>
                    <span className="dash__event-when">{ago(event.occurredAt)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {overview.topChatters.length > 0 && (
            <section className="panel" aria-labelledby="chatters-heading">
              <div className="panel__header">
                <h2 className="panel__title" id="chatters-heading">
                  Most active
                </h2>
                <HelpHint
                  label="how this is counted"
                  tip="Messages Connaro retained from this person over the last seven days, across every connected platform."
                />
              </div>
              <ul className="panel__body dash__events">
                {overview.topChatters.map((chatter) => (
                  <li key={chatter.displayName} className="dash__event">
                    <span className="dash__event-text">{chatter.displayName}</span>
                    <span className="dash__event-when">{chatter.messages.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {/*
       * Orientation for a workspace with nothing connected. It vanishes on
       * its own once the first platform is live — see GettingStarted.
       */}
      {connections === null ? null : (
        <GettingStarted connections={connections} emailVerified={me.user.emailVerified} />
      )}

      <section className="panel" aria-labelledby="platforms-heading">
        <div className="panel__header">
          <h2 className="panel__title" id="platforms-heading">
            Platforms
          </h2>
          <HelpHint
            label="platform status"
            tip="Every platform this deployment can connect. Status comes from the live connection, not from a cached value."
          />
        </div>

        <div className="panel__body">
          {summary === null ? (
            // Space is reserved so the panel does not jump when data lands.
            <p className="dash__loading" aria-live="polite">
              Loading connections…
            </p>
          ) : summary.connected.length === 0 && summary.connectable.length === 0 ? (
            <EmptyState title="No platform is connectable yet">
              Each platform needs its application credentials configured on this deployment before
              it can be connected. <Link to="/connections">See what is available</Link>.
            </EmptyState>
          ) : (
            <>
              <ul className="dash__platforms">
                {(connections ?? [])
                  .filter((entry) => entry.connection !== null || entry.connectable)
                  .map((entry) => (
                    <li
                      key={entry.platform}
                      className={`dash__platform platform-card--${entry.platform}`}
                    >
                      <PlatformIcon platform={entry.platform} displayName={entry.displayName} />
                      <span className="dash__platform-name">{entry.displayName}</span>
                      {entry.connection === null ? (
                        <StatusPill tone="idle" label="Not connected" />
                      ) : (
                        <StatusPill tone={entry.connection.tone} label={entry.connection.label} />
                      )}
                    </li>
                  ))}
              </ul>

              {summary.connectable.length > 0 ? (
                <p className="dash__next">
                  {summary.connectable.length === 1
                    ? '1 more platform is ready to connect.'
                    : `${String(summary.connectable.length)} more platforms are ready to connect.`}{' '}
                  <Link to="/connections">Connect a platform</Link>
                </p>
              ) : null}
            </>
          )}
        </div>
      </section>

      <div className="grid grid--2">
        {/*
        * Workspace and Account moved to Settings.
        *
        * A name, a slug and an email address are REFERENCE, not operations.
        * They occupied the bottom third of the first page after sign-in and
        * answered a question nobody opens a dashboard to ask — §18 of the
        * redesign brief: is my setup ready, is anything broken, what is
        * happening. Settings already carries both.
        */}
      
      </div>
    </>
  );
}
